/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { FormGroup } from 'react-bootstrap'
import { Formik } from 'formik'
import { useServiceContext } from '../../root'
import { Select, type OptionType } from '../../components/Select/Select'
import { ErrorMessageStyled } from '../../components/ErrorMessage'
import { File } from '../../constants/file'
import Modal from '../../components/Modal'
import _ from 'lodash'

interface ShareReportModalProps {
  file: any
  onClose?: () => void
}

interface Entity {
  id: number
  name: string
  chName?: string
  _uid: string
  systemHelperType: 'USER' | 'GROUP'
  type?: string
}

interface Acls {
  userAcls: {
    [key: string]: number[]
  }
  groupAcls: {
    [key: string]: number[]
  }
}

interface AssembledEntities {
  users: Entity[]
  groups: Entity[]
  merged: Entity[]
}

const ShareReportModal: React.FC<ShareReportModalProps> = ({ file, onClose }) => {
  const context = useServiceContext()
  const { api, service } = context
  const [isOpen, setIsOpen] = useState(true)
  const [isDisabled, setIsDisabled] = useState(false)
  const [availableShareEntities, setAvailableShareEntities] = useState<OptionType[]>([])
  const [shareWithList, setShareWithList] = useState<Entity[]>([])
  const [acls, setAcls] = useState<Acls>()
  const [user] = useState(service.auth.getUser())
  const [shareListArray, setShareListArray] = useState<string[]>([])

  useEffect(() => {
    Promise.all([api.users.getAssembledUsersGroups(), api.acls.getFileACL(file.id)]).then(function (result) {
      const entities = result[0] as AssembledEntities
      setAcls(result[1])

      setIsDisabled(() => {
        if (!user) return true

        const userAcls = result[1]['userAcls'] as any
        const groupAcls = result[1]['groupAcls'] as any

        const userCanEdit =
          (userAcls[File.acl.WRITE] || []).indexOf(user.id) >= 0 ||
          (userAcls[File.acl.DELETE] || []).indexOf(user.id) >= 0

        const userGroupIds = _.values(user.groupIds || {})

        const usersGroupCanEdit =
          _.chain([...(groupAcls[File.acl.WRITE] || []), ...(groupAcls[File.acl.DELETE] || [])])
            .intersection(userGroupIds)
            .value().length > 0

        return !(userCanEdit || usersGroupCanEdit)
      })

      setAvailableShareEntities(transformToSelectOptions(entities.merged, '_uid', 'name', null, false, true))
      const adminsGroup = _.find(entities.groups, function (group) {
        return group.id === 1
      })

      let setterArray: Entity[] = []
      if (adminsGroup) {
        setterArray = [adminsGroup, ...setterArray]
      }
      setterArray.push(...getEntitiesWithMinimalAcls(result[1]['userAcls'], entities.users))
      setterArray.push(...getEntitiesWithMinimalAcls(result[1]['groupAcls'], entities.groups))

      setShareWithList(setterArray)
      setShareListArray(_.map(setterArray, '_uid') as string[])
    })
  }, [])

  function transformToSelectOptions(
    arr: Entity[],
    valueKey = 'id',
    labelKey = 'value',
    sortBy: string | null = null,
    preserveOrigVals = false,
    isAdditional = false
  ): OptionType[] {
    if (_.isArray(arr)) {
      if (sortBy && sortBy.length) {
        arr = _.sortBy(arr, (i: any) => {
          if (i[sortBy] && _.isFunction(i[sortBy].toLowerCase)) {
            return i[sortBy].toLowerCase()
          } else {
            return i[sortBy]
          }
        })
      }
      return _.map(arr, function (val: any) {
        return _.isString(val)
          ? {
              value: val,
              label: val
            }
          : {
              ...(preserveOrigVals ? val : {}),
              value: val[valueKey],
              label: val[labelKey],
              additional: isAdditional ? val : null,
              isFixed: val.id === 1
            }
      })
    } else {
      return []
    }
  }

  function save(values: { shareWithList: Entity[]; shareListArray: string[] }) {
    const shareWithListT = values.shareWithList
      .map((element) => {
        if (element.id !== 1) {
          return element
        }
        return undefined
      })
      .filter((item): item is Entity => item !== undefined)

    const sharedWithUsers = _.filter(shareWithListT, { systemHelperType: 'USER' })
    const sharedWithGroups = _.filter(shareWithListT, { systemHelperType: 'GROUP' })

    const aclsT: any = {
      userAcls: transformAclsAccordingToShareList(acls!['userAcls'], sharedWithUsers),
      groupAcls: transformAclsAccordingToShareList(acls!['groupAcls'], sharedWithGroups)
    }

    api.acls.updateFileACL(file.id, aclsT).then(function () {
      doClose()
    })
  }

  function transformAclsAccordingToShareList(aclsT: { [key: string]: number[] }, entities: Entity[]) {
    const ids = _.map(entities, 'id') as number[]
    const assignedIds: number[] = []

    const acls = _.mapValues(aclsT, function (acl: number[], key: string) {
      if (key === File.acl.DELETE) {
        Array.prototype.push.apply(assignedIds, acl)
        // entites in DELETE acl stay unchanged
        return acl
      } else {
        return _.reduce(
          acl,
          function (newAcl: number[], id) {
            if (ids.indexOf(id) >= 0) {
              // removed ids are removed from all acls
              newAcl.push(id)
              assignedIds.push(id)
            }
            return newAcl
          },
          []
        )
      }
    })

    const remainingIds = _.difference(ids, assignedIds)

    // remaining entities are assigned to READ acl
    Array.prototype.push.apply(acls[File.acl.READ], remainingIds)
    return acls
  }

  function getEntitiesWithMinimalAcls(acl: { [key: string]: number[] }, entities: Entity[]): Entity[] {
    return _.chain(acl)
      .omit(File.acl.NONE)
      .reduce(function (arr1: number[], ids) {
        return ids ? arr1.concat(ids) : arr1
      }, [])
      .reduce(function (arr2: Entity[], id) {
        const entity = _.find(entities, { id: id })
        if (entity) {
          arr2.push(entity)
        }
        return arr2
      }, [])
      .value()
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <Modal
        show={isOpen}
        handleClose={doClose}
        title={context.service.filter.translate('BROWSER_SHARE_REPORT_TITLE')}
        size="md"
      >
        <Formik
          enableReinitialize={true}
          initialValues={{ shareWithList, shareListArray }}
          onSubmit={(values) => {
            save(values)
          }}
        >
          {({ setFieldValue, values, handleSubmit }) => (
            <>
              <Modal.Body>
                <FormGroup controlId="3e9732a9-5ca4-471a-8c08-0cdcc44a6420">
                  <Select
                    id="ac6897af-64ed-47c2-aesdcscdcc5-8ab163fe917f"
                    name="shareListArray"
                    placeholder={context.service.filter.translate('SELECT_USER(S)...')}
                    options={availableShareEntities}
                    value={values.shareListArray}
                    onChange={(option: any) => {
                      setFieldValue('shareListArray', option ? _.map(option, 'value') : [])
                      setFieldValue('shareWithList', option ? _.map(option, 'additional') : [])
                    }}
                    multi={true}
                    isDisabled={isDisabled}
                    searchable
                  />
                  <ErrorMessageStyled name="shareListArray" classesName="fs12" />
                </FormGroup>
              </Modal.Body>
              <Modal.Footer>
                <button
                  data-action={'cancel'}
                  className="btn btn-link"
                  onClick={doClose}
                  id="fc29d04b-3fe6-4ee0-9581-ea0c670cf54c"
                >
                  {context.service.filter.translate('CANCEL')}
                </button>
                {!isDisabled && (
                  <button
                    data-action={'save'}
                    className="btn btn-primary text-capitalize"
                    onClick={() => handleSubmit()}
                    id="be78d793-f56c-4cff-89cc-bb8ff47785e3"
                  >
                    {context.service.filter.translate('SAVE')}
                  </button>
                )}
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>
    </>
  )
}

export default ShareReportModal
